/**
 * CL-DRAW-REVIEW-001 — Engineer drawing review + supporting-document classification (pure model).
 * Reuses pmProjectFiles, projectFileShares, reviewRequests, pmFileRevisions — no drawing-register schema.
 */

import {
  REVIEW_REQUEST_STATUSES,
  REVIEW_REQUEST_TYPES,
  fileNeedsAttentionForViewer,
  getOpenReviewRequestForViewer,
} from "./perCollaboratorReviewRequests";
import { compareDrawingFiles, sortReviewRowsByDrawingOrder } from "./drawingOrderModel";

function safeStr(value) {
  return String(value ?? "").trim();
}

function normEmail(value) {
  return safeStr(value).toLowerCase();
}

function toMs(value) {
  if (!value) return 0;
  if (typeof value?.toDate === "function") {
    try {
      const d = value.toDate();
      return d instanceof Date && !Number.isNaN(d.getTime()) ? d.getTime() : 0;
    } catch {
      return 0;
    }
  }
  const d = value instanceof Date ? value : new Date(value);
  return d instanceof Date && !Number.isNaN(d.getTime()) ? d.getTime() : 0;
}

/** Document purpose for engineer / admin supporting uploads (legacy-safe optional field). */
export const ENGINEERING_DOCUMENT_PURPOSE = Object.freeze({
  DRAWING_REVISION: "drawing_revision",
  ENGINEERING_DETAIL: "engineering_detail",
  ENGINEERING_REPORT: "engineering_report",
  ENGINEERING_CALCULATION: "engineering_calculation",
  ENGINEERING_LETTER: "engineering_letter",
  REFERENCE_DOCUMENT: "reference_document",
  PRODUCT_DATA: "product_data",
  OTHER_SUPPORTING_DOCUMENT: "other_supporting_document",
});

export const ENGINEERING_DOCUMENT_PURPOSE_LABELS = Object.freeze({
  [ENGINEERING_DOCUMENT_PURPOSE.DRAWING_REVISION]: "Drawing revision",
  [ENGINEERING_DOCUMENT_PURPOSE.ENGINEERING_DETAIL]: "Engineering detail",
  [ENGINEERING_DOCUMENT_PURPOSE.ENGINEERING_REPORT]: "Engineering report",
  [ENGINEERING_DOCUMENT_PURPOSE.ENGINEERING_CALCULATION]: "Engineering calculation",
  [ENGINEERING_DOCUMENT_PURPOSE.ENGINEERING_LETTER]: "Engineering letter",
  [ENGINEERING_DOCUMENT_PURPOSE.REFERENCE_DOCUMENT]: "Reference document",
  [ENGINEERING_DOCUMENT_PURPOSE.PRODUCT_DATA]: "Product data",
  [ENGINEERING_DOCUMENT_PURPOSE.OTHER_SUPPORTING_DOCUMENT]: "Other supporting document",
});

/** UI chooser options (unique values for selects). */
export const ENGINEERING_SUPPORTING_TYPE_OPTIONS = Object.freeze([
  {
    value: "stamped_drawing_detail",
    purpose: ENGINEERING_DOCUMENT_PURPOSE.ENGINEERING_DETAIL,
    label: "Stamped drawing / detail",
  },
  {
    value: "engineering_report",
    purpose: ENGINEERING_DOCUMENT_PURPOSE.ENGINEERING_REPORT,
    label: "Engineering report",
  },
  {
    value: "calculation_package",
    purpose: ENGINEERING_DOCUMENT_PURPOSE.ENGINEERING_CALCULATION,
    label: "Calculation package",
  },
  {
    value: "letter_assurance",
    purpose: ENGINEERING_DOCUMENT_PURPOSE.ENGINEERING_LETTER,
    label: "Letter / assurance",
  },
  {
    value: "supporting_detail",
    purpose: ENGINEERING_DOCUMENT_PURPOSE.ENGINEERING_DETAIL,
    label: "Supporting detail",
  },
  {
    value: "other_supporting_document",
    purpose: ENGINEERING_DOCUMENT_PURPOSE.OTHER_SUPPORTING_DOCUMENT,
    label: "Other",
  },
  // Legacy keys retained so older uploads still resolve labels/purpose.
  { value: "structural_detail", purpose: ENGINEERING_DOCUMENT_PURPOSE.ENGINEERING_DETAIL, label: "Structural detail" },
  { value: "grade_beam_detail", purpose: ENGINEERING_DOCUMENT_PURPOSE.ENGINEERING_DETAIL, label: "Grade-beam foundation detail" },
  { value: "engineering_sketch", purpose: ENGINEERING_DOCUMENT_PURPOSE.ENGINEERING_DETAIL, label: "Engineering sketch" },
  { value: "calculation", purpose: ENGINEERING_DOCUMENT_PURPOSE.ENGINEERING_CALCULATION, label: "Calculation" },
  { value: "report", purpose: ENGINEERING_DOCUMENT_PURPOSE.ENGINEERING_REPORT, label: "Report" },
  { value: "letter", purpose: ENGINEERING_DOCUMENT_PURPOSE.ENGINEERING_LETTER, label: "Letter" },
  { value: "product_data", purpose: ENGINEERING_DOCUMENT_PURPOSE.PRODUCT_DATA, label: "Product data" },
  { value: "reference_document", purpose: ENGINEERING_DOCUMENT_PURPOSE.REFERENCE_DOCUMENT, label: "Reference document" },
]);

/** Primary chooser options shown in Engineer supporting-document upload UI. */
export const ENGINEERING_SUPPORTING_TYPE_CHOOSER_OPTIONS = Object.freeze(
  ENGINEERING_SUPPORTING_TYPE_OPTIONS.filter((o) =>
    [
      "stamped_drawing_detail",
      "engineering_report",
      "calculation_package",
      "letter_assurance",
      "supporting_detail",
      "other_supporting_document",
    ].includes(o.value)
  )
);

export const ENGINEER_UPLOAD_PATH = Object.freeze({
  REVISED_DRAWING: "revised_drawing",
  STAMPED_DRAWING: "stamped_drawing",
  SUPPORTING_DOCUMENT: "supporting_document",
});

/** Optional purpose discriminator on pmFileRevisions for stamped uploads. */
export const ENGINEERING_REVISION_TYPE = Object.freeze({
  REVISION: "revision",
  STAMPED: "stamped",
});

export const ENGINEER_UPLOAD_ADMIN_STATUS = Object.freeze({
  AWAITING_MARK_REVIEW: "awaiting_mark_review",
  ACCEPTED_AS_CURRENT: "accepted_as_current",
  REVISIONS_STILL_REQUIRED: "revisions_still_required",
  KEPT_AS_SUPPORTING: "kept_as_supporting",
  ARCHIVED_OR_SUPERSEDED: "archived_or_superseded",
});

export function normalizeEngineeringDocumentPurpose(raw) {
  const key = safeStr(raw).toLowerCase();
  if (Object.values(ENGINEERING_DOCUMENT_PURPOSE).includes(key)) return key;
  return "";
}

export function resolvePurposeFromSupportingType(typeKey) {
  const hit = ENGINEERING_SUPPORTING_TYPE_OPTIONS.find((o) => o.value === safeStr(typeKey).toLowerCase());
  return hit?.purpose || ENGINEERING_DOCUMENT_PURPOSE.OTHER_SUPPORTING_DOCUMENT;
}

export function isEngineeringSupportingDocument(file) {
  if (!file || safeStr(file?.status).toLowerCase() === "deleted") return false;
  const purpose = normalizeEngineeringDocumentPurpose(file?.documentPurpose);
  if (purpose && purpose !== ENGINEERING_DOCUMENT_PURPOSE.DRAWING_REVISION) return true;
  if (safeStr(file?.uploadedByRole).toLowerCase() === "engineer" && safeStr(file?.libraryType).toLowerCase() === "client_document") {
    return true;
  }
  if (safeStr(file?.clientUploadSource).toLowerCase() === "engineer_portal") return true;
  return false;
}

export function isDrawingRevisionUploadRecord(revision) {
  return (
    safeStr(revision?.documentPurpose).toLowerCase() === ENGINEERING_DOCUMENT_PURPOSE.DRAWING_REVISION ||
    safeStr(revision?.uploadPath).toLowerCase() === ENGINEER_UPLOAD_PATH.REVISED_DRAWING ||
    (safeStr(revision?.uploaderRole).toLowerCase() === "engineer" && !isEngineeringSupportingDocument(revision))
  );
}

function sheetNumberOf(file) {
  return (
    safeStr(file?.sheetNumber) ||
    safeStr(file?.sheetNo) ||
    safeStr(file?.drawingNumber) ||
    safeStr(file?.sheet) ||
    ""
  );
}

function revisionLabelOf(file) {
  return safeStr(file?.revision) || safeStr(file?.revisionLabel) || safeStr(file?.currentRevision) || "";
}

function markRequestLabel(file, viewerContext, options = {}) {
  const open = getOpenReviewRequestForViewer(file, viewerContext);
  if (open?.requestType === REVIEW_REQUEST_TYPES.REVIEW) {
    return "Request review / comment";
  }
  if (open?.requestType === REVIEW_REQUEST_TYPES.APPROVAL) {
    return "Request decision (unusual for engineers)";
  }
  const status = safeStr(file?.reviewStatus).toLowerCase();
  if (status === "revisions_required") return "Request revised drawing or response";
  if (status === "under_review" || status === "pending_client_review") return "Request review / comment";
  // CL-WORKFLOW-001A: do not call full-project visibility "shared".
  if (safeStr(options?.visibilitySource) === "full_project") {
    return "Available through full-project access";
  }
  if (status === "for_information" || status === "shared" || !open) {
    return "Shared for information";
  }
  return "Shared for information";
}

/**
 * Partition files for Engineering review.
 * CL-ENGINEER-PACKAGE-001: Engineer viewers are always package-driven (shares authoritative).
 * Non-engineer callers may still pass an older accessModel; packageMode defaults on for engineers.
 */
export function buildEngineeringReviewSectionModel({
  files = [],
  reviewViewerContext = null,
  shareByFileId = {},
  shares = null,
  viewerUid = "",
  viewerEmail = "",
  matchedCollaborator = null,
  isHistoricalArchiveProject = false,
  canUploadFiles = false,
  canReviewDrawings = true,
  canViewPhotos = false,
  canViewPlanning = false,
  accessModel = null,
  packageMode = null,
  packageModel = null,
  project = null,
} = {}) {
  const role = safeStr(matchedCollaborator?.role).toLowerCase();
  const usePackage =
    packageMode === true ||
    (packageMode !== false && role === "engineer") ||
    Boolean(packageModel?.packageDriven) ||
    Boolean(accessModel?.packageDriven);
  const orderCards = (cards) => sortReviewRowsByDrawingOrder(cards, project);

  if (usePackage) {
    const pkg = packageModel?.packageDriven
      ? packageModel
      : accessModel?.packageDriven
        ? accessModel
        : buildInlineEngineerPackagePartition({
            files,
            shareByFileId,
            shares,
            reviewViewerContext,
            viewerUid,
            viewerEmail,
            matchedCollaborator,
            canUploadFiles,
            canReviewDrawings,
            canViewPhotos,
            canViewPlanning,
          });

    const toCard = (entry) =>
      buildEngineeringReviewCardModel({
        file: entry.file,
        reviewViewerContext,
        share: entry.share || shareByFileId?.[entry.fileId] || null,
        visibilitySource: "direct_share",
        accessScope: pkg.accessScope,
        canUploadFiles,
        canReviewDrawings,
        packageDriven: true,
      });

    return {
      title: "Engineering review",
      packageDriven: true,
      accessScope: pkg.accessScope,
      sharedFilesOnly: true,
      accessScopeIgnoredForDrawings: true,
      requiringReview: orderCards((pkg.requiringReview || []).map(toCard)),
      forInformation: orderCards((pkg.sharedForInformation || pkg.forInformation || []).map(toCard)),
      completedReviews: orderCards((pkg.completedReviews || []).map(toCard)),
      supportingDocuments: (pkg.supportingDocuments || []).map((e) => e.file || e),
      projectAccess: pkg.projectAccess,
      counts: {
        ...pkg.counts,
        requiringReview: (pkg.requiringReview || []).length,
        forInformation: (pkg.sharedForInformation || pkg.forInformation || []).length,
        completedReviews: (pkg.completedReviews || []).length,
        supportingDocuments: (pkg.supportingDocuments || []).length,
        directlyShared: pkg.directlySharedCount ?? pkg.counts?.directlyShared,
      },
      warnings: [],
      emptyState: pkg.emptyState,
      disclaimer:
        pkg.disclaimer ||
        "Engineer review responses are not client or admin approval. Mark still decides when a revision becomes the current drawing.",
    };
  }

  // Non-engineer / legacy path (unchanged breadth behavior for other personas).
  const access =
    accessModel && Array.isArray(accessModel.filesVisibleToEngineer) ? accessModel : null;

  if (access) {
    const toCard = (entry) => {
      const card = buildEngineeringReviewCardModel({
        file: entry.file,
        reviewViewerContext,
        share: entry.share || shareByFileId?.[entry.fileId] || null,
        visibilitySource: entry.visibilitySource,
        accessScope: access.accessScope,
        canUploadFiles,
        canReviewDrawings,
      });
      return card;
    };
    return {
      title: "Engineering review",
      accessScope: access.accessScope,
      sharedFilesOnly: access.sharedFilesOnly,
      requiringReview: orderCards(access.filesAssignedForReview.map(toCard)),
      forInformation: orderCards(access.filesSharedForInformation.map(toCard)),
      completedReviews: orderCards(access.completedReviews.map(toCard)),
      supportingDocuments: access.supportingDocuments.map((e) => e.file),
      counts: {
        ...access.counts,
        requiringReview: access.filesAssignedForReview.length,
        forInformation: access.filesSharedForInformation.length,
        completedReviews: access.completedReviews.length,
        supportingDocuments: access.supportingDocuments.length,
      },
      warnings: access.warnings || [],
      disclaimer:
        "Engineer review responses are not client or admin approval. Mark still decides when a revision becomes the current drawing.",
    };
  }

  const accessScope = safeStr(matchedCollaborator?.accessScope || "").toLowerCase() || "full_project";
  const sharedFilesOnly =
    !isHistoricalArchiveProject && accessScope && accessScope !== "full_project";

  const list = (Array.isArray(files) ? files : []).filter((f) => safeStr(f?.status).toLowerCase() !== "deleted");
  const requiringReview = [];
  const forInformation = [];
  const completedReviews = [];
  const supportingDocuments = [];

  list.forEach((file) => {
    const fid = safeStr(file?.id);
    const share = shareByFileId?.[fid] || null;
    if (sharedFilesOnly && !share && !isEngineeringSupportingDocument(file)) return;

    if (isEngineeringSupportingDocument(file)) {
      if (sharedFilesOnly && !share) {
        const src = safeStr(file?.clientUploadSource).toLowerCase();
        if (src !== "engineer_portal") return;
      }
      supportingDocuments.push(file);
      return;
    }
    if (!sharedFilesOnly) {
      const lt = safeStr(file?.libraryType).toLowerCase();
      const cat = safeStr(file?.category).toLowerCase();
      if (lt === "photo" || cat === "photos" || cat === "photo") return;
    }

    const open = getOpenReviewRequestForViewer(file, reviewViewerContext);
    const needsAttention = fileNeedsAttentionForViewer(file, reviewViewerContext);
    const card = buildEngineeringReviewCardModel({
      file,
      reviewViewerContext,
      share,
      visibilitySource: share ? "direct_share" : "full_project",
      accessScope,
      canUploadFiles,
      canReviewDrawings,
    });
    if (needsAttention && open) {
      requiringReview.push(card);
      return;
    }
    const responded = (Array.isArray(file?.reviewRequests) ? file.reviewRequests : []).some((r) => {
      if (safeStr(r?.status).toLowerCase() !== REVIEW_REQUEST_STATUSES.RESPONDED) return false;
      const uid = safeStr(viewerUid);
      const email = normEmail(viewerEmail);
      if (uid && safeStr(r?.userId) === uid) return true;
      if (email && normEmail(r?.email) === email) return true;
      return false;
    });
    if (responded || safeStr(file?.reviewStatus).toLowerCase() === "changes_submitted") {
      completedReviews.push(card);
      return;
    }
    forInformation.push(card);
  });

  const orderedRequiring = orderCards(requiringReview);
  const orderedInfo = orderCards(forInformation);
  const orderedCompleted = orderCards(completedReviews);
  supportingDocuments.sort((a, b) => compareDrawingFiles(a?.file || a, b?.file || b));

  return {
    title: "Engineering review",
    accessScope,
    sharedFilesOnly,
    requiringReview: orderedRequiring,
    forInformation: orderedInfo,
    completedReviews: orderedCompleted,
    supportingDocuments,
    counts: {
      requiringReview: requiringReview.length,
      forInformation: forInformation.length,
      completedReviews: completedReviews.length,
      supportingDocuments: supportingDocuments.length,
      directlyShared: Object.keys(shareByFileId || {}).length,
      projectFilesAvailable: requiringReview.length + forInformation.length + completedReviews.length,
      availableViaFullProject: sharedFilesOnly
        ? 0
        : forInformation.filter((c) => c.visibilitySource === "full_project").length +
          requiringReview.filter((c) => c.visibilitySource === "full_project").length +
          completedReviews.filter((c) => c.visibilitySource === "full_project").length,
    },
    warnings: sharedFilesOnly
      ? []
      : [
          "Full project access allows this collaborator to see all eligible project drawings.",
        ],
    disclaimer:
      "Engineer review responses are not client or admin approval. Mark still decides when a revision becomes the current drawing.",
  };
}

/** Inline shares-only partition when packageModel is not precomputed (avoids circular imports). */
function buildInlineEngineerPackagePartition({
  files = [],
  shareByFileId = {},
  shares = null,
  reviewViewerContext = null,
  viewerUid = "",
  viewerEmail = "",
  matchedCollaborator = null,
  canUploadFiles = false,
  canReviewDrawings = true,
  canViewPhotos = false,
  canViewPlanning = false,
} = {}) {
  const uid = safeStr(viewerUid);
  const email = normEmail(viewerEmail);
  let map = { ...(shareByFileId || {}) };
  if (shares && Array.isArray(shares)) {
    map = {};
    shares.forEach((share) => {
      const fid = safeStr(share?.fileId);
      if (!fid) return;
      if (uid && safeStr(share.sharedWithUid) === uid) map[fid] = share;
      else if (email && normEmail(share.sharedWithEmail || share.sharedWithEmailLower) === email) {
        map[fid] = share;
      }
    });
  }
  const accessScope = safeStr(matchedCollaborator?.accessScope || "").toLowerCase() || "full_project";
  const list = (Array.isArray(files) ? files : []).filter((f) => safeStr(f?.status).toLowerCase() !== "deleted");
  const requiringReview = [];
  const sharedForInformation = [];
  const completedReviews = [];
  const supportingDocuments = [];
  const viewerCtx = reviewViewerContext || { uid, email };

  list.forEach((file) => {
    const fid = safeStr(file?.id);
    if (!fid) return;
    const lt = safeStr(file?.libraryType).toLowerCase();
    const cat = safeStr(file?.category).toLowerCase();
    if (lt === "photo" || cat === "photos" || cat === "photo") return;

    const share = map[fid] || null;
    if (isEngineeringSupportingDocument(file)) {
      const src = safeStr(file?.clientUploadSource).toLowerCase();
      const own =
        src === "engineer_portal" ||
        (uid && safeStr(file?.uploadedByUid) === uid) ||
        (email && normEmail(file?.uploadedByEmail) === email);
      if (!share && !own) return;
      supportingDocuments.push({ file, fileId: fid, share });
      return;
    }
    if (!share) return;

    const open = getOpenReviewRequestForViewer(file, viewerCtx);
    const needsAttention = fileNeedsAttentionForViewer(file, viewerCtx);
    const responded = (Array.isArray(file?.reviewRequests) ? file.reviewRequests : []).some((r) => {
      if (safeStr(r?.status).toLowerCase() !== REVIEW_REQUEST_STATUSES.RESPONDED) return false;
      if (uid && safeStr(r?.userId) === uid) return true;
      if (email && normEmail(r?.email) === email) return true;
      return false;
    });
    const entry = {
      file,
      fileId: fid,
      share,
      visibilitySource: "direct_share",
      canComment: Boolean(canReviewDrawings) && share.canComment === true,
      canUploadRevision: Boolean(canUploadFiles) && share.canUploadRevision === true,
    };
    if (needsAttention && open) {
      requiringReview.push(entry);
      return;
    }
    if (responded || safeStr(file?.reviewStatus).toLowerCase() === "changes_submitted") {
      completedReviews.push(entry);
      return;
    }
    sharedForInformation.push(entry);
  });

  const directlySharedCount = requiringReview.length + sharedForInformation.length + completedReviews.length;
  return {
    packageDriven: true,
    accessScope,
    requiringReview,
    sharedForInformation,
    completedReviews,
    supportingDocuments,
    directlySharedCount,
    projectAccess: {
      overview: true,
      photos: Boolean(canViewPhotos),
      planning: Boolean(canViewPlanning),
      engineeringDocumentUpload: Boolean(canUploadFiles),
    },
    counts: {
      drawingsShared: directlySharedCount,
      directlyShared: directlySharedCount,
      requiringReview: requiringReview.length,
      forInformation: sharedForInformation.length,
      sharedForInformation: sharedForInformation.length,
      completedReviews: completedReviews.length,
      supportingDocuments: supportingDocuments.length,
      projectFilesAvailable: directlySharedCount,
      availableViaFullProject: 0,
    },
    emptyState:
      "No drawings have been shared with you yet. Mark will add drawings to your review package when they are ready.",
    disclaimer:
      "Engineer review responses are not client or admin approval. Mark still decides when a revision becomes the current drawing.",
  };
}

export function buildEngineeringReviewCardModel({
  file = null,
  reviewViewerContext = null,
  share = null,
  visibilitySource = "",
  accessScope = "",
  canUploadFiles = false,
  canReviewDrawings = true,
  packageDriven = false,
} = {}) {
  const open = getOpenReviewRequestForViewer(file, reviewViewerContext);
  const hasDirectShare = !!share;
  const scope = safeStr(accessScope).toLowerCase();
  const sharedFilesOnly = packageDriven || (scope && scope !== "full_project");
  const source = packageDriven
    ? "direct_share"
    : safeStr(visibilitySource) || (hasDirectShare ? "direct_share" : sharedFilesOnly ? "direct_share" : "full_project");

  // Package / share gate: never default comment/upload ON when share is missing.
  const canComment = Boolean(canReviewDrawings) && (hasDirectShare ? share.canComment === true : false);
  const canUploadRevision =
    Boolean(canUploadFiles) && (hasDirectShare ? share.canUploadRevision === true : false);
  const canUploadStamped = canUploadRevision;

  return {
    fileId: safeStr(file?.id),
    file,
    title: safeStr(file?.documentDisplayTitle) || safeStr(file?.name) || "Drawing",
    sheetNumber: sheetNumberOf(file),
    revision: revisionLabelOf(file),
    sharedAt: file?.sharedAt || share?.createdAt || file?.updatedAt || null,
    markRequest: markRequestLabel(file, reviewViewerContext, {
      visibilitySource: packageDriven ? "direct_share" : source,
    }),
    dueDate: open?.dueAt || file?.reviewDueAt || null,
    canComment,
    canUploadRevision,
    canUploadStamped,
    openReviewRequest: open || null,
    reviewStatus: safeStr(file?.reviewStatus),
    visibilitySource: source,
    hasDirectShare,
    packageDriven: Boolean(packageDriven),
    visibilityLabel: packageDriven
      ? "In your review package"
      : source === "direct_share"
        ? "Shared directly"
        : source === "full_project"
          ? "Available through full-project access"
          : "Available",
  };
}

export function validateEngineerRevisedDrawingUpload({
  selectedFileId = "",
  revisionLabel = "",
  revisionNotes = "",
  hasFile = false,
  canUploadRevision = false,
  engineeringRevisionType = "",
} = {}) {
  const errors = [];
  if (!safeStr(selectedFileId)) errors.push("Select the existing drawing this revision updates.");
  if (!hasFile) errors.push("Choose a file to upload.");
  if (!canUploadRevision) {
    errors.push(
      safeStr(engineeringRevisionType).toLowerCase() === ENGINEERING_REVISION_TYPE.STAMPED
        ? "Stamped-drawing upload is not enabled for this drawing."
        : "Revision upload is not enabled for this drawing."
    );
  }
  return {
    ok: errors.length === 0,
    errors,
    revisionLabel: safeStr(revisionLabel),
    revisionNotes: safeStr(revisionNotes),
    engineeringRevisionType:
      safeStr(engineeringRevisionType).toLowerCase() === ENGINEERING_REVISION_TYPE.STAMPED
        ? ENGINEERING_REVISION_TYPE.STAMPED
        : ENGINEERING_REVISION_TYPE.REVISION,
    defaultAdminStatus: ENGINEER_UPLOAD_ADMIN_STATUS.AWAITING_MARK_REVIEW,
    becomesCurrentMain: false,
    guidance:
      safeStr(engineeringRevisionType).toLowerCase() === ENGINEERING_REVISION_TYPE.STAMPED
        ? "This stamped drawing is saved as a revision and awaits Mark’s review. It does not automatically replace the current drawing."
        : "This upload is saved as a revision and awaits Mark’s review. It does not automatically replace the current drawing.",
  };
}

export function validateEngineerSupportingDocumentUpload({
  title = "",
  documentType = "",
  hasFile = false,
  canUploadFiles = false,
} = {}) {
  const errors = [];
  if (!canUploadFiles) errors.push("Upload permission is not enabled for this project.");
  if (!safeStr(title)) errors.push("Enter a document title.");
  if (!safeStr(documentType)) errors.push("Choose a document type.");
  if (!hasFile) errors.push("Choose a file to upload.");
  return {
    ok: errors.length === 0,
    errors,
    purpose: resolvePurposeFromSupportingType(documentType),
    defaultAdminStatus: ENGINEER_UPLOAD_ADMIN_STATUS.AWAITING_MARK_REVIEW,
    isDrawingRevision: false,
    guidance:
      "Supporting documents are not revisions of Mark’s drawings. Grade-beam details and similar samples stay separate.",
  };
}

/**
 * Firestore payload for a new engineer supporting document (uses client_document create rules).
 */
export function buildEngineerSupportingDocumentCreatePayload({
  projectId = "",
  title = "",
  documentType = "",
  description = "",
  relatedDrawingFileId = "",
  relatedReviewRequestId = "",
  revisionOrDate = "",
  uploadedByUid = "",
  uploadedByEmail = "",
  url = "",
  path = "",
  fileName = "",
  contentType = null,
  size = null,
} = {}) {
  const purpose = resolvePurposeFromSupportingType(documentType);
  const typeOpt = ENGINEERING_SUPPORTING_TYPE_OPTIONS.find((o) => o.value === safeStr(documentType).toLowerCase());
  return {
    projectId: safeStr(projectId),
    name: safeStr(fileName) || safeStr(title) || "Supporting document",
    url: safeStr(url),
    path: safeStr(path),
    size: typeof size === "number" ? size : null,
    contentType: contentType || null,
    category: "engineering",
    subcategory: safeStr(documentType).toLowerCase() || "supporting",
    phase: "both",
    libraryType: "client_document",
    documentDisplayTitle: safeStr(title) || null,
    documentDescription: safeStr(description) || null,
    documentPurpose: purpose,
    engineeringDocumentType: safeStr(documentType).toLowerCase() || null,
    engineeringDocumentTypeLabel: typeOpt?.label || null,
    relatedDrawingFileId: safeStr(relatedDrawingFileId) || null,
    relatedReviewRequestId: safeStr(relatedReviewRequestId) || null,
    revision: safeStr(revisionOrDate) || null,
    clientVisible: false,
    documentStatus: "active",
    clientUploadSource: "engineer_portal",
    uploadedByUid: safeStr(uploadedByUid) || null,
    uploadedByEmail: safeStr(uploadedByEmail) || null,
    uploadedByRole: "engineer",
    engineerUploadAdminStatus: ENGINEER_UPLOAD_ADMIN_STATUS.AWAITING_MARK_REVIEW,
    createdBy: safeStr(uploadedByUid) || null,
  };
}

/**
 * Extra fields for pmFileRevisions when engineer uploads a revised drawing.
 * Workflow status lives on the revision record (source of truth for Mark disposition).
 */
export function buildEngineerRevisionDocFields({
  revisionLabel = "",
  revisionNotes = "",
  uploaderUid = "",
  uploaderEmail = "",
  engineeringRevisionType = "",
} = {}) {
  const stamped = safeStr(engineeringRevisionType).toLowerCase() === ENGINEERING_REVISION_TYPE.STAMPED;
  return {
    documentPurpose: ENGINEERING_DOCUMENT_PURPOSE.DRAWING_REVISION,
    uploadPath: stamped ? ENGINEER_UPLOAD_PATH.STAMPED_DRAWING : ENGINEER_UPLOAD_PATH.REVISED_DRAWING,
    engineeringRevisionType: stamped ? ENGINEERING_REVISION_TYPE.STAMPED : ENGINEERING_REVISION_TYPE.REVISION,
    revisionLabel: safeStr(revisionLabel) || null,
    note: safeStr(revisionNotes) || null,
    uploaderUid: safeStr(uploaderUid) || null,
    uploaderEmail: safeStr(uploaderEmail) || null,
    uploaderRole: "engineer",
    engineerUploadAdminStatus: ENGINEER_UPLOAD_ADMIN_STATUS.AWAITING_MARK_REVIEW,
    replacedMainFile: false,
  };
}

export function labelEngineeringRevisionType(revision = {}) {
  if (safeStr(revision?.engineeringRevisionType).toLowerCase() === ENGINEERING_REVISION_TYPE.STAMPED) {
    return "Stamped drawing";
  }
  if (safeStr(revision?.uploadPath).toLowerCase() === ENGINEER_UPLOAD_PATH.STAMPED_DRAWING) {
    return "Stamped drawing";
  }
  return "Revised drawing";
}

/**
 * Collaborator-allowed parent pmProjectFiles patch after a revision upload.
 * Must match firestore.rules reviewer/share update allowlist — do not add engineerUploadAdminStatus.
 */
export const ENGINEER_REVISED_DRAWING_PARENT_UPDATE_KEYS = Object.freeze([
  "reviewResponseType",
  "reviewResponseAt",
  "reviewResponseByUid",
  "reviewResponseByEmail",
  "reviewResponseNote",
  "reviewStatus",
  "updatedAt",
]);

export function buildEngineerRevisedDrawingParentFilePatch({
  uploaderUid = "",
  uploaderEmail = "",
  revisionNotes = "",
  fileName = "",
  updatedAt = null,
} = {}) {
  return {
    reviewResponseType: "revision_uploaded",
    reviewResponseAt: updatedAt,
    reviewResponseByUid: safeStr(uploaderUid) || null,
    reviewResponseByEmail: safeStr(uploaderEmail) || null,
    reviewResponseNote: safeStr(revisionNotes) || `Uploaded version: ${safeStr(fileName) || "file"}`,
    reviewStatus: "changes_submitted",
    updatedAt,
  };
}

export function assertEngineerRevisedDrawingParentPatchCompatible(patch = {}) {
  const keys = Object.keys(patch || {});
  const disallowed = keys.filter((k) => !ENGINEER_REVISED_DRAWING_PARENT_UPDATE_KEYS.includes(k));
  return {
    ok: disallowed.length === 0,
    disallowed,
    keys,
  };
}

/**
 * Plan for Mark accepting a collaborator revision as the current main file.
 * Does not mutate — caller performs archive + promote writes.
 * Disposition status source of truth: pmFileRevisions.engineerUploadAdminStatus.
 */
export function buildAcceptCollaboratorRevisionAsCurrentPlan({
  file = null,
  revision = null,
} = {}) {
  const fileId = safeStr(file?.id);
  const revisionId = safeStr(revision?.id);
  const revUrl = safeStr(revision?.url);
  if (!fileId || !revisionId || !revUrl) {
    return { ok: false, reason: "missing_file_or_revision", patch: null };
  }
  if (revision?.replacedMainFile === true && safeStr(file?.url) === revUrl) {
    return { ok: false, reason: "already_current", patch: null };
  }
  return {
    ok: true,
    reason: "ready",
    archivePreviousMain: Boolean(safeStr(file?.url) && safeStr(file?.url) !== revUrl),
    previousMain: {
      url: safeStr(file?.url) || null,
      name: safeStr(file?.name) || null,
      path: safeStr(file?.path) || null,
      contentType: file?.contentType || null,
      size: typeof file?.size === "number" ? file.size : null,
    },
    revisionPatch: {
      replacedMainFile: true,
      engineerUploadAdminStatus: ENGINEER_UPLOAD_ADMIN_STATUS.ACCEPTED_AS_CURRENT,
    },
    filePatch: {
      url: revUrl,
      name: safeStr(revision?.name || revision?.fileName || file?.name) || "File",
      path: safeStr(revision?.storagePath || revision?.path) || safeStr(file?.path) || null,
      contentType: revision?.contentType || file?.contentType || null,
      size: typeof revision?.size === "number" ? revision.size : file?.size ?? null,
      revision: safeStr(revision?.revisionLabel || revision?.revision) || safeStr(file?.revision) || null,
      reviewStatus: "under_review",
    },
    autoApproves: false,
  };
}

export function buildAdminEngineerDrawingReadinessModel({
  row = null,
  collaboratorsDirty = false,
  inviteStatus = "",
  sharedFileCount = 0,
  openReviewCount = 0,
  supportingDocCount = 0,
} = {}) {
  const email = normEmail(row?.email || row?.emailLower);
  const roleOk = safeStr(row?.role).toLowerCase() === "engineer";
  const items = [
    {
      id: "contact",
      ok: Boolean(email || safeStr(row?.name)),
      label: "Engineer contact saved",
      detail: email || safeStr(row?.name) || "Add name/email",
    },
    {
      id: "collaborator",
      ok: !collaboratorsDirty && Boolean(email) && roleOk,
      label: "Collaborator saved to project",
      detail: collaboratorsDirty ? "Unsaved changes" : roleOk ? "Engineer on this project" : "Set role to Engineer and save",
    },
    {
      id: "invite",
      ok: Boolean(safeStr(inviteStatus)),
      label: "Invitation status",
      detail: safeStr(inviteStatus) || "No invite yet",
    },
    {
      id: "shared",
      ok: sharedFileCount > 0,
      label: "Shared drawings",
      detail: sharedFileCount > 0 ? `${sharedFileCount} file(s) shared` : "No drawings shared yet",
    },
    {
      id: "reviews",
      ok: openReviewCount > 0,
      label: "Open review requests",
      detail: openReviewCount > 0 ? `${openReviewCount} open` : "None open — engineer may only see information shares",
    },
    {
      id: "upload",
      ok: Boolean(row?.permissions?.canUploadFiles !== false && (row?.permissions?.canReviewDrawings !== false)),
      label: "Upload / review permissions",
      detail: "Confirm comment + revision upload on shares; Apply Engineer defaults if needed",
    },
    {
      id: "supporting",
      ok: true,
      label: "Supporting documents uploaded",
      detail: supportingDocCount > 0 ? `${supportingDocCount} from engineer` : "None yet",
    },
  ];
  return {
    items,
    warnings: items.filter((i) => !i.ok && i.id !== "supporting" && i.id !== "invite").map((i) => `${i.label}: ${i.detail}`),
  };
}

export function engineerUploadDoesNotAutoApprove() {
  return true;
}

export const ENGINEER_REVISION_DISPOSITION = Object.freeze({
  REQUEST_FURTHER_REVISION: "request_further_revision",
  KEEP_AS_SUPPORTING: "keep_as_supporting",
  ARCHIVE: "archive",
});

/**
 * Mark follow-up on a collaborator revision without promoting it to current.
 * Upload is never treated as approval.
 */
export function buildEngineerRevisionDispositionPlan({
  action = "",
  file = null,
  revision = null,
} = {}) {
  const key = safeStr(action).toLowerCase();
  const fileId = safeStr(file?.id);
  const revisionId = safeStr(revision?.id);
  if (!fileId || !revisionId) {
    return { ok: false, reason: "missing_file_or_revision", revisionPatch: null, filePatch: null };
  }
  if (key === ENGINEER_REVISION_DISPOSITION.REQUEST_FURTHER_REVISION) {
    return {
      ok: true,
      action: key,
      autoApproves: false,
      revisionPatch: {
        engineerUploadAdminStatus: ENGINEER_UPLOAD_ADMIN_STATUS.REVISIONS_STILL_REQUIRED,
        replacedMainFile: false,
      },
      filePatch: {
        reviewStatus: "revisions_required",
      },
      entryBody: "Requested further revision from collaborator upload (not accepted as current).",
    };
  }
  if (key === ENGINEER_REVISION_DISPOSITION.KEEP_AS_SUPPORTING) {
    return {
      ok: true,
      action: key,
      autoApproves: false,
      revisionPatch: {
        engineerUploadAdminStatus: ENGINEER_UPLOAD_ADMIN_STATUS.KEPT_AS_SUPPORTING,
        replacedMainFile: false,
        documentPurpose: ENGINEERING_DOCUMENT_PURPOSE.OTHER_SUPPORTING_DOCUMENT,
      },
      filePatch: null,
      entryBody: "Kept collaborator upload as supporting (not accepted as current drawing).",
    };
  }
  if (key === ENGINEER_REVISION_DISPOSITION.ARCHIVE) {
    return {
      ok: true,
      action: key,
      autoApproves: false,
      revisionPatch: {
        engineerUploadAdminStatus: ENGINEER_UPLOAD_ADMIN_STATUS.ARCHIVED_OR_SUPERSEDED,
        replacedMainFile: false,
        archived: true,
      },
      filePatch: null,
      entryBody: "Archived collaborator upload (prior main unchanged; not approval).",
    };
  }
  return { ok: false, reason: "unknown_action", revisionPatch: null, filePatch: null };
}

export function listEngineeringSupportingDocumentsForAdmin(files = []) {
  return (Array.isArray(files) ? files : [])
    .filter((f) => safeStr(f?.status).toLowerCase() !== "deleted")
    .filter((f) => isEngineeringSupportingDocument(f))
    .sort((a, b) => toMs(b.createdAt || b.updatedAt) - toMs(a.createdAt || a.updatedAt));
}

export function labelEngineerUploadAdminStatus(status) {
  const key = safeStr(status).toLowerCase();
  if (key === ENGINEER_UPLOAD_ADMIN_STATUS.AWAITING_MARK_REVIEW) return "Awaiting Mark review";
  if (key === ENGINEER_UPLOAD_ADMIN_STATUS.ACCEPTED_AS_CURRENT) return "Accepted as current";
  if (key === ENGINEER_UPLOAD_ADMIN_STATUS.REVISIONS_STILL_REQUIRED) return "Revisions still required";
  if (key === ENGINEER_UPLOAD_ADMIN_STATUS.KEPT_AS_SUPPORTING) return "Kept as supporting upload";
  if (key === ENGINEER_UPLOAD_ADMIN_STATUS.ARCHIVED_OR_SUPERSEDED) return "Archived or superseded";
  return key ? key.replace(/_/g, " ") : "—";
}
